// Small controls shared by the practice dock's panels.

import React from 'react';
import classNames from 'classnames';
import { KeyHint } from './shortcuts';

// −, the tempo, + and Tap. The tempo is the dock's, shared by the metronome
// and the backing track.
export function TempoControl({ transport, large = false }) {
    return (
        <div className={classNames('practice-tempo', { 'practice-tempo--large': large })}>
            <button type="button" className="practice-step" onClick={() => transport.nudgeTempo(-1)} aria-label={gettext('Slower')}>−<KeyHint k="−" /></button>
            <span className="bpm"><strong>{transport.bpm}</strong> BPM</span>
            <button type="button" className="practice-step" onClick={() => transport.nudgeTempo(1)} aria-label={gettext('Faster')}>+<KeyHint k="+" /></button>
            <button type="button" className="practice-pill" onClick={transport.tapTempo}>{gettext('Tap')}<KeyHint k="Enter" /></button>
        </div>
    );
}

// A row of pills of which one is chosen.
export function Segmented({ label, value, options, onChange }) {
    return (
        <div className="practice-segmented" role="radiogroup" aria-label={label}>
            {options.map(option => (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={value === option.value}
                    aria-label={option.title}
                    title={option.title}
                    className={classNames('practice-pill', { active: value === option.value })}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

export function MicLevel({ level }) {
    const width = Math.min(100, Math.max(0, (Math.log10(level + 1e-6) + 3.5) / 3 * 100));
    return (
        <span className="practice-level" title={gettext('Microphone level')}>
            <span style={{ width: `${width}%` }} />
        </span>
    );
}

// What went wrong with the microphone, in words, or null.
export function micProblem(micState) {
    return {
        requesting: gettext('Allow the microphone so it can hear which chord you play.'),
        denied: gettext('The microphone is blocked. Allow it from the address bar.'),
        unsupported: gettext("This browser can't listen."),
        error: gettext("Couldn't open the microphone."),
    }[micState] || null;
}

// Diagram of a chord as the site draws it for the player's instrument.
// shortcut: the key that plays it, shown in a corner; hint: a key shown
// only with the other key hints (see KeyHint).
export function ChordDiagram({ chord, name, className, onClick, label, shortcut, hint }) {
    const content = (
        <>
            {hint && <KeyHint k={hint} />}
            {shortcut && <kbd className="practice-chord-key" aria-hidden="true">{shortcut}</kbd>}
            <span className="practice-chord-name">{name}</span>
            {chord && chord.svg_code ?
                <span className="practice-chord-svg" dangerouslySetInnerHTML={{ __html: chord.svg_code }} />
                :
                <span className="practice-chord-svg practice-chord-missing" />
            }
            {label && <span className="practice-chord-label">{label}</span>}
        </>
    );
    return onClick ?
        <button
            type="button"
            className={classNames('practice-chord', className)}
            onClick={onClick}
            aria-keyshortcuts={shortcut}
            title={shortcut ? `${gettext('Hear it')} (${shortcut})` : gettext('Hear it')}
        >
            {content}
        </button>
        :
        <div className={classNames('practice-chord', className)}>{content}</div>;
}
